import { loadBarChart } from 'actions/chartsActions';
import { fetchPOI } from 'actions/pois';
import { useAppDispatch, useAppSelector } from 'index';
import { ChartDataPoints, ChartModel, ChartResponse } from 'Interfaces/ChartInterface';
import { JSONInterface } from 'Interfaces/JsonInterface';
import * as React from 'react';
import { Card, Col, FormControl, Row } from 'react-bootstrap';
import ChartBoxComponent from 'views/Charts/ChartBoxComponent';
import ChartSettings from 'views/Charts/ChartOptions/ChartSettings';
import * as ChartUtils from '../../utils';


interface Props {
  model: ChartModel;
  chartDataPoints: ChartDataPoints;
  onModelChange: (model: ChartModel) => void;
}


export default function ChartComponent(props: Props) {
  const dispatch = useAppDispatch();
  const forms = useAppSelector(state => state.forms.collection);
  const [form] = React.useState(null);
  const locations = useAppSelector(state => state.locations.collection);
  const { model } = props;
  const signal = React.useRef<AbortController | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [charts, setCharts] = React.useState<JSONInterface[] | null>(null);
  const [chartData, setChartData] = React.useState<ChartResponse[] | null>(null);

  React.useEffect(() => {
    if (!loading && !charts) {
      setLoading(true);
      loadChart();
    }
  }, [props.model]);

  React.useEffect(() => {
    if (charts && chartData) {
      const newCharts = ChartUtils.createCharts(model, chartData, form, forms, locations, props.chartDataPoints);
      setCharts(newCharts);
    }
  }, [
    model.verticalBars, model.showMissingXAxisNumericValues,
    model.linearAverage, model.movingAverage, model.averageInput
  ]);

  React.useEffect(() => {
    if (charts && chartData) {
      if (model.type === 'RADAR') {
        const newCharts = ChartUtils.createCharts(model, chartData, form, forms, locations, props.chartDataPoints);
        setCharts(newCharts);
      } else {
        setLoading(true);
        loadChart();
      }
    }
  }, [model.average, model.excludeNull]);

  React.useEffect(() => {
    if (charts && chartData) {
      const newCharts = ChartUtils.createCharts(model, chartData, form, forms, locations, props.chartDataPoints);
      setCharts(newCharts);
    }
  }, [props.chartDataPoints, chartData]);

  const loadChart = () => {
    if (model.type === 'RADAR') {
      if (!signal.current) {
        signal.current = new AbortController();
      }
      const query = {
        id: model.dataPoints ? model.dataPoints.join(',') : []
      };
      const formIds = Object.keys(model.forms);
      const response = dispatch(fetchPOI(
        formIds[0], signal.current.signal, undefined, undefined, query,
        ['Name', 'questionnaire_id'].concat(model.forms[formIds[0]].questions).join(',')
      ));
      response.then(res => res.json()).then(json => {
        if (!json.errorCode) {
          chartLoaded(json);
        } else {
          setCharts([]);
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        setCharts([]);
      });
    } else {
      const loadPromise = dispatch(loadBarChart(model));
      loadPromise.then(response => response.json()).then(json => {
        chartLoaded(json);
      }).catch(error => {
        console.log(error);
        setLoading(false);
        setCharts([]);
      });
    }
  };

  const chartLoaded = (chartData) => {
    setChartData(chartData);
    const newCharts = ChartUtils.createCharts(model, chartData, form, forms, locations, {});
    setCharts(newCharts);
  };

  const switchChart = (id: string, action: boolean, name?: string) => {
    if (chartData && charts) {
      if (name?.startsWith('sort')) {
        const chart = chartData.find((cd) => cd.id === id);
        if (chart) {
          const newChart = ChartUtils.getBarChart(
            model, chart, forms, undefined, locations, undefined, action, name
          );
          const newCharts = charts.map(ch => {
            if (ch.responseId !== id) {
              return ch;
            }
            return newChart[0];
          });
          setCharts(newCharts);
        }
      } else if (name === 'cummulative') {
        if (model.combine === 'FIELD') {
          const charts = ChartUtils.getCombinedLineChart(model, chartData, forms, action, locations);
          setCharts(charts);
        } else if (model.combine === 'LOCATION') {
          const charts = ChartUtils.getCombinedLocationLineChart(
            model, chartData, forms, action, locations, {}
          );
          setCharts(charts);
        } else {
          const chart = chartData.find((cd) => cd.id === id);
          if (chart) {
            const newChart = ChartUtils.getLineChart(model, chart, forms, action, locations, undefined);
            const newCharts = charts.map((ch) => {
              if (ch.responseId === id) {
                return newChart;
              }
              return ch;
            });
            setCharts(newCharts);
          }
        }
        return;
      }
    }
  };

  const chartBox = React.useCallback(() => {
    return charts ? (
      <div className={`row ${model.type === 'RADAR' ? 'justify-content-center' : ''}`}>
        {charts.map((chart, index) => {
          return (
            <ChartBoxComponent
              key={`${model.id}-chart-${chart['id']}-${index}-${chart['responseId']}`}
              id={chart['responseId']}
              index={index}
              chartType={model.type}
              options={chart}
              switchChart={switchChart}
              addChartSvgs={() => console.log('add chart svg')}
              model={model}
            />
          );
        })}
      </div>
    ) : null;
  }, [charts]);

  return (
    <Card className="chart-panel">
      <Card.Header>
        <Row>
          <Col md={7}>
            <FormControl
              size={'sm'}
              value={model.name}
              type="text"
              onChange={(e) => props.onModelChange({...model, name: e.target.value})}
            />
          </Col>
          <Col>
            <ChartSettings
              model={model}
              onModelUpdate={(m) => props.onModelChange(m)}
            />
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {chartBox()}
      </Card.Body>
    </Card>
  );
}
